<script setup lang="ts">
import { computed, onMounted, type Ref, ref, watch } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import Pagination from "@/commons/components/Pagination.vue";
import { useInstitutionStore } from "@/domains/home/services/nir/associate-registers/stores";
import { useFormsStore } from "@/domains/home/services/nir/configurations/stores/forms.ts";
import { EntityType } from "@/domains/home/services/nir/configurations/types/forms.ts";
import router from "@/router.ts";
import PhoneInput from "@/components/PhoneInput/PhoneInput.vue";
import { useNotificationsStore } from "@/app/notify/notifications.ts";
import api from "@/app/services/api.ts";

type InstitutionForm = {
  name: string;
  code: string;
  typeIds: string[];
  contactPersonName: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
};

type UserCheckResponse = { status: number; message: string; data: any[] };

const createModalOpen = ref(false);
const loading = ref(false);
const page = ref(1);
const limit = ref(12);
const totalItems = ref(5);
const parentIdFilter = ref("");
const checkResults = ref<UserCheckResponse>({
  status: 0,
  message: "",
  data: []
});

const store = useInstitutionStore();
const formsStore = useFormsStore();
const notify = useNotificationsStore();

onMounted(() => {
  fetch();
});

function fetch() {
  loading.value = true;
  formsStore.fetchEntityTypes(parentIdFilter.value);
  formsStore.fetchAvailableEntityTypes();
  store.fetchInstitutions(page.value, limit.value).then(() => {
    loading.value = false;
  });
}

// Organize entity types hierarchically
const organizedEntityTypes = computed(() => {
  if (!formsStore.entityTypes) return [];

  const rootTypes = formsStore.entityTypes.filter((type) => !type.parentId);

  function buildHierarchy(parent: EntityType, level = 0): any[] {
    const children =
      formsStore.entityTypes?.filter((type) => type.parentId === parent.id) ||
      [];
    const result = [{ ...parent, level }];

    children.forEach((child) => {
      result.push(...buildHierarchy(child, level + 1));
    });

    return result;
  }

  const organized: any[] = [];
  rootTypes.forEach((root) => {
    organized.push(...buildHierarchy(root));
  });

  return organized;
});

function getAllParentIds(typeId: string): string[] {
  const parentIds: string[] = [];
  const entityType = formsStore.entityTypes?.find((type) => type.id === typeId);

  if (entityType && entityType.parentId) {
    parentIds.push(entityType.parentId);
    // Recursively get all ancestor parents
    parentIds.push(...getAllParentIds(entityType.parentId));
  }

  return parentIds;
}

function getAllChildIds(typeId: string): string[] {
  const childIds: string[] = [];
  const directChildren =
    formsStore.entityTypes?.filter((type) => type.parentId === typeId) || [];

  directChildren.forEach((child) => {
    childIds.push(child.id);
    // Recursively get all descendant children
    childIds.push(...getAllChildIds(child.id));
  });

  return childIds;
}

function toggleEntityType(typeId: string) {
  const index = form.value.typeIds.indexOf(typeId);

  if (index > -1) {
    // Deselect: Remove this type and all its children
    const typesToRemove = [typeId, ...getAllChildIds(typeId)];
    form.value.typeIds = form.value.typeIds.filter(
      (id) => !typesToRemove.includes(id)
    );
  } else {
    // Select: Add this type, all its parents, and all its children
    const typesToAdd = [
      typeId,
      ...getAllParentIds(typeId),
      ...getAllChildIds(typeId)
    ];

    // Add types without duplications
    typesToAdd.forEach((id) => {
      if (!form.value.typeIds.includes(id)) {
        form.value.typeIds.push(id);
      }
    });
  }
}

function isTypeSelected(typeId: string) {
  return form.value.typeIds.includes(typeId);
}

const form: Ref<InstitutionForm> = ref({
  name: "",
  code: "",
  typeIds: [],
  contactPersonName: "",
  contactEmail: "",
  contactPhone: "",
  address: ""
});

function validateForm(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!form.value.name || form.value.name.length < 4) {
    errors.push("Institution Name is required");
  }

  if (!form.value.code || form.value.code.length < 2) {
    errors.push("Abbreviation/Acronym is required");
  }

  if (
    !form.value.contactPersonName ||
    form.value.contactPersonName.length < 4
  ) {
    errors.push("Contact Person Name is required");
  }

  if (!form.value.contactEmail || form.value.contactEmail.trim() === "") {
    errors.push("Contact Email is required");
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.value.contactEmail.trim())) {
      errors.push("Contact Email must be a valid email address");
    }
  }

  if (!form.value.contactPhone || form.value.contactPhone.trim() === "") {
    errors.push("Contact Phone is required");
  } else {
    const phone = form.value.contactPhone.replace(/[\s\-\(\)]/g, ""); // Remove spaces, dashes, brackets

    const ugandanPhoneRegex =
      /^(\+?256|0)?(7[0-9]{8}|3[0-9]{8})$|^(7[0-9]{8}|3[0-9]{8})$/;

    if (!ugandanPhoneRegex.test(phone)) {
      errors.push(
        "Contact Phone must be a valid Ugandan phone number (e.g., +256701234567, 0701234567, or 701234567)"
      );
    }
  }

  if (!form.value.address || form.value.address.length < 4) {
    errors.push("Physical Address is required");
  }

  if (!form.value.typeIds || form.value.typeIds.length === 0) {
    errors.push("At least one Entity Type must be selected");
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

type AccountCheckStatus = "idle" | "loading" | "success" | "error";
const accountCheckStatus = ref<AccountCheckStatus>("idle");
const linkToAccount = ref(false);
const accountCheckMessage = ref("success");
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

async function checkAccountExists() {
  accountCheckStatus.value = "loading";
  try {
    const response = await api.get(
      "/auth/api/users/user-account-exists-by-email-or-phone",
      {
        params: {
          email: form.value.contactEmail,
          phone: form.value.contactPhone.replace("+", "")
        }
      }
    );
    const body = response.data as UserCheckResponse;
    checkResults.value = body;
    if (body.status === 200) {
      accountCheckStatus.value = "error";
      accountCheckMessage.value = body.message;
    } else {
      accountCheckStatus.value = "success";
      accountCheckMessage.value = body.message;
    }
  } catch (error: any) {
    accountCheckStatus.value = "success";
    accountCheckMessage.value =
      error.response?.data?.message ?? "Unable to verify account details";
  }
}

watch(
  () => [form.value.contactEmail, form.value.contactPhone] as const,
  ([email, phone]) => {
    accountCheckStatus.value = "idle";
    accountCheckMessage.value = "";
    if (debounceTimer) clearTimeout(debounceTimer);
    if (email && phone) {
      debounceTimer = setTimeout(checkAccountExists, 600);
    }
  },
);

function createInstitution() {
  loading.value = true;

  const validation = validateForm();

  if (!validation.isValid) {
    loading.value = false;
    validation.errors.forEach((error) => {
      notify.error(error);
    });
    return;
  }

  form.value.contactPhone = form.value.contactPhone.split("+").join("");
  store
    .createInstitution(form.value)
    .then(() => {
      loading.value = false;
      resetForm();
      createModalOpen.value = false;
      fetch();
    })
    .catch(() => {
      loading.value = false;
    });
}

function resetForm() {
  form.value = {
    name: "",
    code: "",
    typeIds: [],
    contactPersonName: "",
    contactEmail: "",
    contactPhone: "",
    address: ""
  };
  accountCheckStatus.value = "idle";
  accountCheckMessage.value = "";
}

function viewInstitution(id: string) {
  router.push({ name: "nir-associate-register", params: { id: id } });
}

function update(pg: number) {
  page.value = pg;
  fetch();
}
</script>

<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-white p-4 rounded shadow">
    <!-- Action Bar -->
    <div class="flex items-center justify-between mb-4">
      <div class="grid grid-cols-3 gap-2">
        <input
          type="text"
          class="pl-10 pr-4 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary w-64"
          placeholder="Search institutions..."
        />
      </div>

      <button
        @click="createModalOpen = true"
        class="btn-primary bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded text-sm font-medium transition flex items-center space-x-2"
      >
        <i class="fa-solid fa-plus"></i>
        <span>Add Institution</span>
      </button>
    </div>

    <!-- Institutions Table -->
    <div class="bg-white rounded shadow flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">
                Name
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">
                Types
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">
                Status
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">
                Has Account
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">
                Actions
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">
                Direct Admission
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">
                Interim ERN
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700">
                Registers Affiliations
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="institution in store.institutions"
              :key="institution.id"
              class="border-b border-gray-100 hover:bg-gray-50 transition"
            >
              <td class="py-3 px-4">
                <div class="flex items-center space-x-2">
                  <div
                    class="w-8 h-8 bg-primary-100 rounded flex items-center justify-center flex-shrink-0"
                  >
                    <i class="fa-solid fa-landmark text-blue-600 text-xs"></i>
                  </div>
                  <div>
                    <p class="font-medium text-gray-900">
                      {{ institution.name }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ institution.address }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="py-3 px-4">
                <div class="flex gap-2">
                  <span
                    v-for="(type, idx) in institution.types.slice(0, 3)"
                    :key="idx"
                    class="bg-primary-100 text-primary-500 px-2 rounded-lg"
                  >{{ type }}</span
                  >
                  <label class="px-1" v-if="institution.types.length > 3"
                  >...</label
                  >
                  <label class="bg-primary text-white rounded-lg px-3">{{
                      institution.types.length
                    }}</label>
                </div>
              </td>
              <td class="py-3 px-4">
                <span
                  :class="[
                    'px-2 py-1 text-xs rounded-full',
                    institution.approvedAt
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800',
                  ]"
                >
                  {{ institution.status }}
                </span>
              </td>
              <td class="py-3 px-4">
                <label v-if="institution.hasAccount" class="text-primary">
                  <i class="fa fa-check"></i> Yes
                </label>
                <label v-else class="text-red-600">
                  <i class="fa fa-times"></i> No
                </label>
              </td>
              <td class="py-3 px-4">
                <div class="flex items-center space-x-2">
                  <button
                    class="btn-sm-primary"
                    @click="viewInstitution(institution.id)"
                    title="View Details"
                  >
                    <i class="fa-solid fa-eye"></i> View
                  </button>
                </div>
              </td>
              <td class="py-3 px-4">
                <span
                  :class="[
                    'px-2 py-1 text-xs rounded-full',
                    institution.hasDirectAdmission
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-500',
                  ]"
                >
                  <i
                    :class="
                      institution.hasDirectAdmission
                        ? 'fas fa-check-circle'
                        : 'fas fa-ban'
                    "
                  ></i>
                  {{ institution.hasDirectAdmission ? "Enabled" : "Disabled" }}
                </span>
              </td>
              <td class="py-3 px-4">
                <span
                  :class="[
                    'px-2 py-1 text-xs rounded-full',
                    institution.hasInterimErn
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-500',
                  ]"
                >
                  <i
                    :class="
                      institution.hasInterimErn
                        ? 'fas fa-check-circle'
                        : 'fas fa-ban'
                    "
                  ></i>
                  {{ institution.hasInterimErn ? "Enabled" : "Disabled" }}
                </span>
              </td>
              <td class="py-3 px-4">
                <span
                  :class="[
                    'px-2 py-1 text-xs rounded-full',
                    institution.registerExisting
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-500',
                  ]"
                >
                  <i
                    :class="
                      institution.registerExisting
                        ? 'fas fa-check-circle'
                        : 'fas fa-ban'
                    "
                  ></i>
                  {{ institution.registerExisting ? "Enabled" : "Disabled" }}
                </span>
              </td>
            </tr>
            <!-- Empty State -->
            <tr v-if="!store.institutions || store.institutions.length === 0">
              <td colspan="7" class="py-12 text-center">
                <div
                  class="flex flex-col items-center justify-center text-gray-500"
                >
                  <i
                    class="fa-solid fa-landmark text-4xl mb-3 text-gray-300"
                  ></i>
                  <p class="text-lg font-medium">No institutions found</p>
                  <p class="text-sm">Add a new institution to get started</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Pagination -->
      <Pagination
        :pagination="store.pagination"
        :page="page"
        :limit="limit"
        label="institutions"
        @update="update"
      />
    </div>

    <!-- Create Institution Modal -->
    <AppModal v-model="createModalOpen" xl6>
      <div class="p-2">
        <h2 class="text-lg font-bold text-gray-900 mb-4">
          Add New Associate Register
        </h2>

        <form @submit.prevent="createInstitution" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col">
              <div class="grid grid-cols-2 gap-4">
                <!-- Institution Name -->
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <i class="fa-solid fa-landmark text-gray-400 mr-1"></i>
                    Institution Name *
                  </label>
                  <input
                    v-model="form.name"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="e.g., Uganda Registration Services Bureau"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
                    Abbreviation/Acronym. *
                  </label>
                  <input
                    v-model="form.code"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="e.g., URSB"
                  />
                </div>

                <!-- Contact Email -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <i class="fa-solid fa-user text-gray-400 mr-1"></i>
                    Contact Person Name *
                  </label>
                  <input
                    v-model="form.contactPersonName"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <i class="fa-solid fa-envelope text-gray-400 mr-1"></i>
                    Contact Email *
                  </label>
                  <input
                    v-model="form.contactEmail"
                    type="email"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="info@institution.go.ug"
                  />
                </div>

                <!-- Contact Phone -->
                <div>
                  <PhoneInput
                    v-model="form.contactPhone"
                    label="Phone"
                    defaultCountry="+256"
                    placeholder="7xxxxxx"
                    required
                    size="sm"
                  />
                </div>

                <!-- Account check result -->
                <div
                  class="col-span-2"
                  v-if="form.contactEmail && form.contactPhone"
                >
                  <div
                    v-if="accountCheckStatus === 'loading'"
                    class="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2"
                  >
                    <i class="fa-solid fa-spinner fa-spin text-primary"></i>
                    Checking account...
                  </div>
                  <div
                    v-else-if="accountCheckStatus === 'success'"
                    class="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2"
                  >
                    <i class="fa-solid fa-circle-check"></i>
                    {{ accountCheckMessage }}
                  </div>
                  <div
                    v-else-if="accountCheckStatus === 'error'"
                    class="flex justify-between gap-2 text-sm bg-red-50 border border-red-200 rounded-t px-3 py-2"
                  >
                    <span class="text-red-700">
                      <i class="fa-solid fa-circle-exclamation"></i>
                      {{ accountCheckMessage }}
                    </span>

                    <span
                      class="text-green-700 cursor-pointer bg-green-50 p-1 rounded-lg"
                      @click="linkToAccount = !linkToAccount"
                    >
                      <i class="fas fa-check-square" v-if="linkToAccount"></i>
                      <i class="fa-regular fa-square" v-else></i>
                      Link to Account
                    </span>
                  </div>
                  <template
                    v-for="(checkResult, idx) in checkResults.data"
                    :key="idx"
                  >
                    <div
                      v-if="checkResult.linkable"
                      class="flex flex-col gap-2 text-sm bg-red-50 border border-red-200 rounded-b px-3 py-2"
                    >
                      <span
                      >One or both of the account details provided are already
                        used by <b>{{ checkResult.partner.name }}</b></span
                      >
                      <span
                      >Email: <i>{{ checkResult.partner.email }}</i></span
                      >
                      <span
                      >Phone: <i>{{ checkResult.partner.phoneNumber }}</i></span
                      >
                    </div>
                  </template>
                </div>

                <!-- Address -->
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1.5">
                    <i
                      class="fa-solid fa-map-marker-alt text-gray-400 mr-1"
                    ></i>
                    Physical Address *
                  </label>
                  <textarea
                    v-model="form.address"
                    required
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Enter full physical address"
                  ></textarea>
                </div>
              </div>
            </div>
            <div class="flex flex-col">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fa-solid fa-layer-group text-gray-400 mr-1"></i>
                Entity Types
              </label>
              <div
                class="border border-gray-300 rounded p-3 bg-gray-50 max-h-64 overflow-y-auto"
              >
                <div v-if="organizedEntityTypes.length > 0" class="space-y-1">
                  <template
                    v-for="entityType in organizedEntityTypes"
                    :key="entityType.id"
                  >
                    <div
                      v-if="
                        formsStore.availableEntityTypes?.some(
                          (a) => a.id === entityType.id,
                        )
                      "
                      class="flex items-center p-2 rounded transition hover:bg-gray-100 cursor-pointer"
                      @click="toggleEntityType(entityType.id)"
                    >
                      <input
                        type="checkbox"
                        :checked="isTypeSelected(entityType.id)"
                        class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                        @click.stop="toggleEntityType(entityType.id)"
                      />
                      <span
                        class="ml-2 text-sm text-gray-700 flex items-center"
                        :style="{ paddingLeft: `${entityType.level * 20}px` }"
                      >
                        <i
                          v-if="entityType.level > 0"
                          class="fa-solid fa-arrow-turn-up fa-rotate-90 text-gray-400 mr-2 text-xs"
                        ></i>
                        {{ entityType.name }}
                      </span>
                    </div>
                  </template>
                </div>
                <div v-else class="text-sm text-gray-500 text-center py-4">
                  <i class="fa-solid fa-info-circle mr-1"></i>
                  No entity types available
                </div>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                <i class="fa-solid fa-info-circle mr-1"></i>
                Select the entity types that this institution can manage.
                <span
                  v-if="form.typeIds.length > 0"
                  class="font-medium text-primary-600"
                >
                  ({{ form.typeIds.length }} selected)
                </span>
              </p>
            </div>
          </div>

          <!-- Form Actions -->
          <div
            class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200"
          >
            <button
              type="button"
              @click="
                createModalOpen = false;
                resetForm();
              "
              class="btn-danger"
            >
              <i class="fas fa-times mr-1"></i>
              Cancel
            </button>

            <button
              type="submit"
              v-if="linkToAccount"
              class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-check mr-1"></i>
              <span v-if="!loading"
              >Create Associate Register (Linked to MDA)</span
              >
              <span v-else>Creating...</span>
            </button>
            <button
              v-else
              type="submit"
              :disabled="
                loading ||
                (!!form.contactEmail &&
                  !!form.contactPhone &&
                  accountCheckStatus != 'success')
              "
              class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-check mr-1"></i>
              <span v-if="!loading">Create Associate Register</span>
              <span v-else>Creating...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}
</style>
